import React, { RefObject, useCallback, useState } from 'react';
import styles from './Notifications.module.scss';
import { Stack } from 'new/elements/Stack';
import { Select } from 'new/elements/Select';
import { Button } from 'new/elements/Button';
import { Refresh } from '@mui/icons-material';
import { useResourceNotifications } from 'new/hooks/adapters/useResourceNotifications';
import { useDrawerContext } from 'new/components/drawer/useDrawerContext';
import { useDispatch } from 'react-redux';
import { fetchResourceNotificationsPaginated } from 'js/actions';
import { NotificationsListView } from 'new/elements/Notifications/NotificationsListView';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_RESOURCE_NOTIFICATIONS_REQUEST } from 'js/constants/actionTypes';

interface ListProps {
  items: [];
  resourceId: number;
  resourceType: string;
  parentScrollRef?: RefObject<any>;
}

const sortItems = [
  {
    title: 'Most recent first',
    value: 'DESC',
  },
  {
    title: 'Oldest first',
    value: 'ASC',
  },
];

export const List = (props: ListProps) => {
  const { resourceId, resourceType, parentScrollRef } = props;
  const loading = useIsLoading(GET_RESOURCE_NOTIFICATIONS_REQUEST);
  const { items, meta } = useResourceNotifications(resourceType, resourceId);
  const { perPageCount, currentPage, numPagesCount, totalItemsCount, sortOrder } = meta;
  const [sort, setSort] = useState(sortOrder || 'DESC');
  const dispatch = useDispatch();
  const { drawerContentRef } = useDrawerContext();

  const loadMoreItems = useCallback(
    (page) => {
      if (page && !loading) {
        dispatch(
          fetchResourceNotificationsPaginated(
            resourceId,
            resourceType,
            {
              page,
              per_page: perPageCount || 100,
              sort_order: sort,
              sort_by: 'created_at',
            },
            null,
          ),
        );
      }
    },
    [currentPage, numPagesCount, loading, resourceType, resourceId, perPageCount],
  );

  const onChangeSort = (e) => {
    const { value } = e.target;
    setSort(value);
    dispatch(
      fetchResourceNotificationsPaginated(resourceId, resourceType, {
        page: 1,
        per_page: perPageCount || 100,
        sort_order: value,
        sort_by: 'created_at',
      }),
    );
  };

  const onRefresh = () => {
    dispatch(
      fetchResourceNotificationsPaginated(resourceId, resourceType, {
        page: 1,
        per_page: perPageCount || 100,
        sort_order: sort,
        sort_by: 'created_at',
      }),
    );
  };

  return (
    <Stack>
      <div className={styles.listActions}>
        <Select
          size="small"
          label="Sort by"
          value={sort}
          onChange={onChangeSort}
          sx={{ minWidth: '150px' }}
          items={sortItems}
        />
        <Button onClick={onRefresh} variant="text" startIcon={<Refresh />}>
          Refresh
        </Button>
      </div>
      <NotificationsListView
        items={items}
        loading={loading}
        resourceId={resourceId + ''}
        resourceType={resourceType}
        totalCount={totalItemsCount}
        onPageChange={loadMoreItems}
        currentPage={currentPage}
        pageCount={numPagesCount}
      />
    </Stack>
  );
};
